/**
 * Special offer block — <special-offer-tiers>
 *
 * The radios themselves are not submitted. The selected quantity is written into the
 * `.quantity__input` fields the theme already knows about, because ProductForm resolves the
 * quantity two different ways:
 *
 *   - plain add to cart      → `new FormData(form)`, i.e. the field named `quantity`
 *   - gifts/upsells in cart  → `#Quantity-Form-{section} .quantity__input`.value, and the
 *                              form's own `quantity` is deleted
 *
 * Driving the input covers both, and keeps exactly one `quantity` in the request.
 * This element also recalculates every tier price when the shopper switches variant.
 */

class SpecialOfferTiers extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.updatePrices = this.dataset.updatePrices === 'true';
    this.variants = this.readVariants();

    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);

    if (this.updatePrices && this.variants.length > 1) {
      this.scope = this.closest('product-info') || this.closest('.shopify-section') || document;
      this.onVariantChange = this.onVariantChange.bind(this);
      this.scope.addEventListener('change', this.onVariantChange);
      this.currentVariantId = this.getVariantId();
    }

    this.syncQuantityInput();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    if (this.scope) this.scope.removeEventListener('change', this.onVariantChange);
    if (this.variantTimer) clearTimeout(this.variantTimer);
  }

  readVariants() {
    const script = this.querySelector('[data-special-offer-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent) || [];
    } catch (error) {
      return [];
    }
  }

  get productForm() {
    return this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
  }

  onChange(event) {
    if (!event.target.matches('.special-offer__input')) return;
    this.syncQuantityInput();
  }

  /* The selects/pills live outside this element, so wait a frame for the theme to write the
     new variant id into the form before reading it — then check again once the section has
     had time to re-render after a fetch. */
  onVariantChange(event) {
    if (event.target.closest('special-offer-tiers')) return;
    requestAnimationFrame(() => this.renderIfVariantChanged());
    if (this.variantTimer) clearTimeout(this.variantTimer);
    this.variantTimer = setTimeout(() => this.renderIfVariantChanged(), 400);
  }

  getVariantId() {
    const form = this.productForm;
    const input = form ? form.querySelector('input[name="id"]') : null;
    return input ? input.value : null;
  }

  renderIfVariantChanged() {
    const variantId = this.getVariantId();
    if (!variantId || variantId === this.currentVariantId) return;
    this.currentVariantId = variantId;

    const variant = this.variants.find((item) => String(item.id) === String(variantId));
    if (variant) this.render(variant);
  }

  render(variant) {
    this.querySelectorAll('.special-offer__tier').forEach((tier) => {
      const amounts = this.tierAmounts(tier, variant);

      tier.querySelectorAll('[data-special-offer-text]').forEach((node) => {
        node.innerHTML = this.renderText(node.dataset.specialOfferText, amounts);
      });

      const compare = tier.querySelector('.special-offer__compare');
      if (compare) compare.classList.toggle('hidden', amounts.compare_price <= amounts.price);
    });
  }

  /* Mirrors the Liquid maths in snippets/special-offer-block.liquid */
  tierAmounts(tier, variant) {
    const quantity = parseInt(tier.dataset.quantity, 10) || 1;
    const percentLeft = (100 - (parseFloat(tier.dataset.percentOff) || 0)) / 100;
    const fixedOff = (parseFloat(tier.dataset.fixedOff) || 0) * 100;

    const price = Math.round(variant.price * quantity * percentLeft - fixedOff);
    const useCompareAt =
      tier.dataset.compareSource === 'compare_price' && variant.compare_at_price > variant.price;
    const comparePrice = (useCompareAt ? variant.compare_at_price : variant.price) * quantity;
    const saved = comparePrice - price;

    return {
      quantity: quantity,
      price: price,
      compare_price: comparePrice,
      amount_saved: saved,
      amount_saved_rounded: Math.round(saved / 100) * 100,
      price_each: Math.round(price / quantity),
      compare_price_each: Math.round(comparePrice / quantity),
    };
  }

  /* Mirrors snippets/text-with-price.liquid: [quantity] is always substituted, then the first
     money token that matches wins and gets wrapped in a <span>. Like the Liquid snippet, the
     merchant's text is written out as-is so inline markup keeps working. */
  renderText(text, amounts) {
    let output = String(text == null ? '' : text).replace(/\[quantity\]/g, amounts.quantity);

    const tokens = [
      'price',
      'compare_price',
      'amount_saved',
      'amount_saved_rounded',
      'price_each',
      'compare_price_each',
    ];

    for (let i = 0; i < tokens.length; i++) {
      const token = '[' + tokens[i] + ']';
      if (output.indexOf(token) === -1) continue;
      const money = '<span>' + this.formatMoney(amounts[tokens[i]]) + '</span>';
      return output.split(token).join(money);
    }

    return output;
  }

  /* Liquid's `money_without_trailing_zeros`: whole amounts drop the decimals entirely. */
  formatMoney(cents) {
    const value = Math.max(0, Math.round(cents));
    let format = this.moneyFormat;

    if (value % 100 === 0) {
      format = format
        .replace('amount_with_comma_separator', 'amount_no_decimals_with_comma_separator')
        .replace('amount_with_space_separator', 'amount_no_decimals_with_space_separator')
        .replace('amount_with_period_and_space_separator', 'amount_no_decimals_with_space_separator')
        .replace('amount_with_apostrophe_separator', 'amount_no_decimals')
        .replace(/\bamount\b/, 'amount_no_decimals');
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
      switch (name) {
        case 'amount':
          return this.number(value, 2, ',', '.');
        case 'amount_no_decimals':
          return this.number(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return this.number(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return this.number(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return this.number(value, 2, "'", '.');
        case 'amount_with_space_separator':
          return this.number(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return this.number(value, 0, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return this.number(value, 2, ' ', '.');
        default:
          return match;
      }
    });
  }

  number(cents, decimals, thousands, decimalSeparator) {
    const amount = (cents / 100).toFixed(decimals);
    const parts = amount.split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts[1] ? whole + decimalSeparator + parts[1] : whole;
  }

  /* Every quantity field the theme may read: the one inside `#Quantity-Form-{section}` (used by
     the gifts/upsells path) plus anything owned by this product form (used by a plain submit).
     Usually these are the same element. */
  get quantityInputs() {
    const inputs = new Set();

    const picker = document.getElementById('Quantity-Form-' + this.dataset.section);
    if (picker) picker.querySelectorAll('.quantity__input').forEach((input) => inputs.add(input));

    document.querySelectorAll('.quantity__input').forEach((input) => {
      if (input.form && input.form.id === this.dataset.formId) inputs.add(input);
    });

    return [...inputs];
  }

  syncQuantityInput() {
    const selected = this.querySelector('.special-offer__input:checked');
    if (!selected) return;

    this.quantityInputs.forEach((input) => {
      if (input.value === selected.value) return;
      input.value = selected.value;
      /* Our own hidden field has no listeners; a visible quantity selector needs the event
         so the theme refreshes its own state. */
      if (!input.hasAttribute('data-special-offer-quantity')) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
}

if (!customElements.get('special-offer-tiers')) {
  customElements.define('special-offer-tiers', SpecialOfferTiers);
}
